import type {
  PreparedItemAttachment,
  PreparedItemDetailIngredient,
  PreparedItemDetails,
} from "../types/preparedItemDetails.types";
import type {
  PreparedItemDetailsApiResponse,
  PreparedItemDetailsIngredientApi,
  PreparedItemMediaApi,
} from "../types/preparedItemDetailsApi.types";

function mapIngredient(
  ingredient: PreparedItemDetailsIngredientApi,
  index: number,
): PreparedItemDetailIngredient {
  const unit = ingredient.base_uom?.short_name ?? ingredient.base_uom?.name ?? "";

  return {
    id: `${ingredient.id}-${index}`,
    ingredientId: String(ingredient.id),
    sku: ingredient.sku,
    name: ingredient.name,
    baseUnit: unit,
    quantity: ingredient.qty ?? 0,
    quantityUnit: unit,
    currentAcc: ingredient.average_current_cost,
    currentAccUnit: unit,
    estimatedCost: ingredient.est_ingredient_cost,
    notes: ingredient.notes?.trim() || undefined,
  };
}

function mapPreparationSteps(steps: string): string[] | undefined {
  const trimmed = steps.trim();
  if (!trimmed) return undefined;

  const lines = trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  return lines.length > 0 ? lines : [trimmed];
}

function getMediaType(
  filename: string,
  mimeType?: string,
): PreparedItemAttachment["type"] {
  const lowerName = filename.toLowerCase();
  const lowerMime = mimeType?.toLowerCase() ?? "";

  if (lowerMime.includes("pdf") || lowerName.endsWith(".pdf")) {
    return "pdf";
  }

  return "image";
}

function mapMediaAttachment(
  media: PreparedItemMediaApi,
): PreparedItemAttachment | null {
  const filePath = media.file_path?.trim() || media.url?.trim() || "";
  if (!filePath) return null;

  const originFilename =
    media.origin_filename?.trim() || `attachment-${media.id}`;

  return {
    id: String(media.id),
    type: getMediaType(originFilename, media.mime_type),
    origin_filename: originFilename,
    file_path: filePath,
  };
}

export function mapPreparedItemDetailsResponse(
  data: PreparedItemDetailsApiResponse,
): PreparedItemDetails {
  const outputUnit =
    data.output_uom?.short_name ?? data.output_uom?.name ?? "";
  const attachments = (data.media ?? [])
    .map(mapMediaAttachment)
    .filter((attachment): attachment is PreparedItemAttachment => attachment != null);

  return {
    id: String(data.id),
    nameEn: data.name_en || data.name,
    nameAr: data.name_ar || data.name,
    sku: data.sku,
    status: data.status === "inactive" ? "inactive" : "active",
    isSetupComplete: data.is_finished,
    categoryId: data.category_id,
    categoryName: data.category ?? "",
    subcategoryId: data.subcategory_id,
    subcategoryName: data.subcategory ?? "",
    outputUomId: data.output_uom?.id,
    outputUom: outputUnit,
    canBeUsedInRecipes: data.can_use_recipes,
    storageCondition: data.storage_condition,
    estimatedQuantity: data.financial_summary?.est_qty ?? 0,
    estimatedQuantityUnit: outputUnit,
    totalEstimatedIngredientsCost: 
      data.financial_summary?.total_est_ingredients_cost,
    estimatedCostPerOutputUom: data.financial_summary?.est_cost_output_uom,
    ingredients: (data.ingredients ?? []).map(mapIngredient),
    defaultPreparationTime: data.default_preparation_time?.trim() || undefined,
    preparationInstructions: mapPreparationSteps(data.preparation_steps),
    qualityNotes: data.quality_notes?.trim() || undefined,
    attachments: attachments.length > 0 ? attachments : undefined,
    activityLog: data.audit_logs ?? [],
    perLevel: data.par_level ?? 0,
  };
}
